import type { CancelDisposition, WithdrawalQueueRow, WithdrawalStatus } from '@pf/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Ban, Banknote, FileUp, Hourglass, Search, Upload, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import {
  SLIP_CONTENT_TYPES,
  SLIP_MAX_BYTES,
  useCancelQueuedWithdrawal,
  useOperatorSubmitEvidence,
  useWithdrawalQueue,
} from '@/lib/api/ops-queries';
import { bankInfo } from '@/lib/banks';
import { nowMs, toEpochMs } from '@/lib/clock';
import { formatAge, formatDateTime, formatDateTimeCompact } from '@/lib/datetime';
import { readParam, useDebouncedParam, useListState } from '@/lib/nav/list-state';
import {
  WITHDRAWAL_LIST,
  WITHDRAWAL_SORTS,
  WITHDRAWAL_TABS,
  type WithdrawalTab,
} from '@/lib/nav/ops-lists';
import { ALL_WEBS, useWebScope } from '@/lib/scope/web-scope';
import { stateMeta } from '@/lib/states';
import { cn, shortId } from '@/lib/utils';
import { MoneyDisplay } from '@/components/domain/money-display';
import { ParticipantPair } from '@/components/domain/participant-chip';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { StateBadge } from '@/components/domain/state-badge';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader } from '@/components/layout/page';
import { Pager } from '@/components/layout/pager';
import { SortControl } from '@/components/layout/sort-control';
import { Button } from '@/components/ui/button';
import { Callout, EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import {
  Input,
  Label,
  Pill,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
} from '@/components/ui/primitives';

/**
 * The withdrawal pool — who is waiting for a match, who is mid-flight, who is done.
 *
 * WAITING is the page's reason to exist: these are customers whose money is locked on their
 * website while the pool waits for a depositor at their amount, and the queue is sorted
 * oldest-first because the longest wait is the first thing an operator should wonder about.
 * Search, the status filter, the sort and the pager all run server-side: the pool can outgrow
 * any single fetch, and a filter that only looked at the fetched page would quietly miss the
 * rest.
 *
 * The view itself lives in the URL (`@/lib/nav/list-state`), so the tab an operator is working
 * survives opening a row, the back button, and a link sent to a colleague.
 */

/** The wire statuses inside each tab — the status filter only offers the current tab's. */
const GROUP_STATUSES: Record<WithdrawalTab, readonly WithdrawalStatus[]> = {
  WAITING: ['AVAILABLE'],
  IN_FLIGHT: ['RESERVED', 'LOCKED', 'ON_HOLD'],
  // MERGED (0047) sits with the other terminals rather than getting a tab: a folded slice is a
  // fragment that stopped existing, and the amount an operator would look for is on the origin
  // row — which is in WAITING, where it belongs.
  DONE: ['FULFILLED', 'CANCELLED', 'EXPIRED', 'MERGED'],
};

/**
 * How long a row may sit in one place before it is worth looking at first — an hour in the pool,
 * or an hour reserved without a slip. Neither is an incident on its own, which is why this is a
 * highlight and not a badge.
 */
const ATTENTION_AFTER_MS = 60 * 60_000;

/** The order the group's tab reads best in: WAITING is a queue, the other two are logs. */
function defaultDir(group: WithdrawalTab): 'ASC' | 'DESC' {
  return group === 'WAITING' ? 'ASC' : 'DESC';
}

/** A sort key from the table header or the mobile control, ignored unless the queue offers it. */
const readSort = readParam.oneOf(WITHDRAWAL_SORTS);

/**
 * The origin's own wallet lock reference, recovered from a slice's `<origin>#<n>` reference
 * without a lookup — `lock_reference` never changes on the parent when it is split (migration
 * 0034), so a child's ref is always exactly the origin's, with `#<split_sequence>` appended.
 * Falls back to the slice's own ref if the suffix does not match what is expected, which only a
 * pre-upgrade API response (optional fields absent) or a shape the console has not seen before
 * could cause — never silently hides the reference the operator needs.
 */
function originLockReferenceOf(row: WithdrawalQueueRow): string | null {
  if (row.origin_withdrawal_id == null) return null;
  const ref = row.lock_reference ?? null;
  if (ref == null) return null;
  if (row.split_sequence == null) return ref;
  const suffix = `#${String(row.split_sequence)}`;
  return ref.endsWith(suffix) ? ref.slice(0, -suffix.length) : ref;
}

/** The Withdrawal queue page — see the module docs above. */
export function WithdrawalsPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scope } = useWebScope();
  const view = useListState(WITHDRAWAL_LIST);
  const { tab: group, status, q, amount, sort, page, limit } = view.state;
  // No `dir` in the URL means "this tab's natural order", so the tab switch does not have to
  // remember which direction to substitute — it just drops the param.
  const dir = view.state.dir ?? defaultDir(group);
  const queue = useWithdrawalQueue({ group, status, q, amount, sort, dir, page, limit });

  // The search runs over the whole pool server-side, so it is charged per keystroke unless the
  // box waits for the operator to stop typing. `q` still comes from the URL — the hook only
  // holds the last few hundred milliseconds of it — so back, "clear filters" and a return from a
  // detail all put the box back in step with the view they restored.
  const [searchDraft, onSearchChange] = useDebouncedParam(q, (next) => {
    view.set({ q: next, page: 0 });
  });

  // Cancelling on the customer's behalf — MAKER-grade (`can('MAKER')` is true for CHECKER too,
  // mirroring the server's `withdrawal:cancel`). The dialog is the reason gate: the action
  // releases the customer's funds lock, so it never fires from a bare row click. It also carries
  // the reservation warning, because the same button on a RESERVED row cancels a depositor who
  // may already have transferred — see the actions column for why LOCKED and ON_HOLD are not
  // offered at all.
  const { can } = useAuth();
  const canCancel = can('MAKER');
  const cancel = useCancelQueuedWithdrawal();
  const [cancelTarget, setCancelTarget] = useState<WithdrawalQueueRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  // Which of the two cancels the operator picked in the actions column. Both release the same
  // funds lock and land the row CANCELLED; they differ only in what the *website* is asked to do
  // next with the money — leave it in the customer's wallet, or re-file it as an ordinary
  // withdrawal in its own payout queue for the customer who cannot do that themselves. Held here
  // rather than passed at confirm time so the dialog can say which act is about to happen.
  const [cancelDisposition, setCancelDisposition] = useState<CancelDisposition>('RELEASE');
  const cancelIsMove = cancelDisposition === 'MOVE_TO_NORMAL';
  // A reservation cancel is a different act from cancelling a row nobody has touched, and the
  // dialog says so — see `withdrawalQueue.cancel.reservedRisk`.
  const cancelIsReserved = cancelTarget?.status === 'RESERVED';
  const closeCancelDialog = (): void => {
    setCancelTarget(null);
    setCancelReason('');
    setCancelDisposition('RELEASE');
    cancel.reset();
  };
  const openCancelDialog = (row: WithdrawalQueueRow, disposition: CancelDisposition): void => {
    setCancelTarget(row);
    setCancelDisposition(disposition);
  };

  // Filing the slip on the customer's behalf — same MAKER grade (the server's `evidence:submit`),
  // for when the customer cannot upload it themselves or their website's flow is broken. Offered
  // on RESERVED and LOCKED rows: those are the states whose deposit may still accept a slip
  // (RESERVED pre-mark-paid, LOCKED while AWAITING_EVIDENCE). The queue row cannot see the
  // deposit's own state, so the server stays the real gate — a deposit already verifying answers
  // 409, which the dialog surfaces and the queue re-fetch resolves.
  const upload = useOperatorSubmitEvidence();
  const [uploadTarget, setUploadTarget] = useState<WithdrawalQueueRow | null>(null);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const slipInputRef = useRef<HTMLInputElement>(null);
  const slipError =
    slipFile === null
      ? null
      : !(SLIP_CONTENT_TYPES as readonly string[]).includes(slipFile.type)
        ? ('invalidType' as const)
        : slipFile.size > SLIP_MAX_BYTES
          ? ('tooLarge' as const)
          : null;
  const closeUploadDialog = (): void => {
    setUploadTarget(null);
    setSlipFile(null);
    upload.reset();
  };

  // Header click: same column flips the direction, a new column starts at its natural order —
  // the tab's own for the queue column, largest-first for amount.
  const sortBy = (key: string): void => {
    const next = readSort(key);
    if (next === undefined) return;
    view.set(
      next === sort
        ? { dir: dir === 'ASC' ? 'DESC' : 'ASC', page: 0 }
        : { sort: next, dir: next === 'CREATED' ? undefined : 'DESC', page: 0 },
    );
  };

  // A slice's row links back to the withdrawal it was cut from — the console only observes split
  // fill, so "view the origin" is a search, not a fetch of a row that might no longer be on this
  // page. Scoped to the tab already on screen, same as every other search here: if the origin
  // has moved to a different group since (still queued vs. long since fulfilled), the empty
  // state gives the same "try another tab" nudge search already gives everywhere on this page.
  const viewOrigin = (originId: string): void => {
    view.set({ status: 'ALL', q: originId, amount: '', page: 0 });
  };

  const rows = queue.data?.items ?? [];
  const counts = queue.data?.counts;
  const total = queue.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));
  const filtered = status !== 'ALL' || q !== '' || amount !== '';
  const at = nowMs();

  const columns: Column<WithdrawalQueueRow>[] = [
    {
      id: 'withdrawal',
      header: t('withdrawalQueue.columns.withdrawal'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-medium">{shortId(row.id)}</span>
            <StateBadge machine="withdrawal" status={row.status} size="sm" />
            {row.status === 'CANCELLED' && row.cancelled_by != null ? (
              // Who cancelled — the customer through their website, or an admin from this very
              // queue. Read off the `withdrawal.released` audit event; rows whose event predates
              // the cause simply show the plain badge.
              <span className="text-2xs text-muted-foreground">
                {t(
                  row.cancelled_by === 'OPERATOR'
                    ? 'states.cancelledBy.OPERATOR'
                    : 'states.cancelledBy.PLAYER',
                )}
              </span>
            ) : null}
          </div>
          <ParticipantPair depositor={row.depositor_participant} withdrawer={row.participant} />
          {row.origin_withdrawal_id != null ? (
            // A slice: link back to the withdrawal it was cut from, and name the wallet lock a
            // website's support desk would be quoting on the phone — never the child's own
            // `<origin>#<n>` ref, which means nothing to them.
            <button
              type="button"
              className="block truncate text-2xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                if (row.origin_withdrawal_id != null) viewOrigin(row.origin_withdrawal_id);
              }}
            >
              {t('withdrawalQueue.split.sliceLabel', { sequence: row.split_sequence ?? '?' })}{' '}
              <span className="font-mono" title={originLockReferenceOf(row) ?? undefined}>
                {shortId(originLockReferenceOf(row) ?? row.origin_withdrawal_id)}
              </span>
            </button>
          ) : null}
        </div>
      ),
    },
    {
      id: 'amount',
      header: t('withdrawalQueue.columns.amount'),
      align: 'right',
      priority: 'primary',
      sortKey: 'AMOUNT',
      cell: (row) => {
        // A shrunk parent: `origin_amount` is what the website actually locked, `amount` is
        // what is left after what has been sliced off. Showing the residual alone is the bug
        // this whole feature exists to fix — an operator sorting by amount would be reading the
        // pool as if the shrinkage never happened. A slice never shows this: its own
        // `origin_amount` equals its `amount` (it was created at that amount, never rewritten),
        // so there is nothing to contrast — its lineage lives in the withdrawal cell instead.
        const originAmount = row.origin_amount;
        const isShrunkParent =
          row.origin_withdrawal_id == null &&
          originAmount != null &&
          originAmount.value !== row.amount.value;
        if (!isShrunkParent) return <MoneyDisplay amount={row.amount} />;
        return (
          <div className="space-y-0.5">
            <div className="flex items-center justify-end gap-1.5">
              <MoneyDisplay
                amount={originAmount}
                size="sm"
                className="text-muted-foreground line-through"
              />
              <span aria-hidden="true" className="text-2xs text-muted-foreground">
                →
              </span>
              <MoneyDisplay amount={row.amount} />
            </div>
            {row.split_count != null && row.split_count > 0 ? (
              <p className="text-right text-2xs text-muted-foreground">
                {t('withdrawalQueue.split.slicesBadge', { count: row.split_count })}
              </p>
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'payee',
      header: t('withdrawalQueue.columns.payee'),
      priority: 'secondary',
      cell: (row) => (
        <div className="min-w-0 space-y-0.5">
          <p className="truncate text-xs font-medium">{row.name}</p>
          <p className="truncate text-2xs text-muted-foreground">
            {bankInfo(row.bank).name} · <span className="font-mono">{row.number_masked}</span>
          </p>
        </div>
      ),
    },
    {
      id: 'username',
      header: t('withdrawalQueue.columns.username'),
      priority: 'secondary',
      // The website's display handle for the withdrawer when it has sent one (`account_display`),
      // else the opaque account_ref. Null ref on rows predating migration 0020.
      cell: (row) =>
        row.account_display != null ? (
          <span className="truncate text-xs font-medium">{row.account_display}</span>
        ) : row.account_ref == null ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <span className="truncate font-mono text-xs">{row.account_ref}</span>
        ),
    },
    // In-flight only, and placed ahead of pool age on purpose: once a depositor is holding the
    // row, "how long has it been reserved" and "has the slip arrived" are the two questions an
    // operator has, and total time in the pool answers neither. It reads as "this customer has
    // been waiting nine hours" when the reservation is nine minutes old.
    ...(group === 'IN_FLIGHT'
      ? [
          {
            id: 'reservation',
            header: t('withdrawalQueue.columns.reservation'),
            priority: 'secondary',
            cell: (row) => {
              // `undefined` and `null` are different answers here. Undefined means an API that
              // predates these fields sent nothing, so the console knows nothing and says so;
              // null means the API looked and there is genuinely no slip — which is the normal
              // state of a RESERVED row and the whole distinction this column exists to draw.
              const reservedAt = row.reserved_at;
              const evidenceAt = row.evidence_submitted_at;
              if (reservedAt === undefined && evidenceAt === undefined) {
                return <span className="text-xs text-muted-foreground">—</span>;
              }
              const held =
                reservedAt == null ? null : at - toEpochMs(reservedAt) > ATTENTION_AFTER_MS;
              return (
                <div className="min-w-0 space-y-0.5">
                  {reservedAt == null ? (
                    <span className="block text-xs text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={cn(
                        'block text-xs font-medium tabular-nums',
                        held === true && 'font-semibold text-state-attention',
                      )}
                    >
                      {formatAge(reservedAt, at)}
                    </span>
                  )}
                  {evidenceAt === undefined ? null : evidenceAt === null ? (
                    <span className="block text-2xs font-medium text-state-attention">
                      {t('withdrawalQueue.reservation.noEvidence')}
                    </span>
                  ) : (
                    <span
                      className="block text-2xs text-muted-foreground"
                      title={formatDateTime(evidenceAt)}
                    >
                      {t('withdrawalQueue.reservation.evidenceAt', {
                        time: formatDateTimeCompact(evidenceAt),
                      })}
                    </span>
                  )}
                </div>
              );
            },
          } satisfies Column<WithdrawalQueueRow>,
        ]
      : []),
    {
      id: 'age',
      header: t('withdrawalQueue.columns.waiting'),
      priority: 'secondary',
      sortKey: 'CREATED',
      cell: (row) => {
        // An hour in the pool is not an incident, but it is the row to look at first. Only on
        // WAITING: on the in-flight tab the reservation column beside it is the headline, and two
        // competing highlighted durations in one row teach an operator to read neither.
        const slow = group === 'WAITING' && at - toEpochMs(row.created_at) > ATTENTION_AFTER_MS;
        return (
          <span
            className={cn(
              'text-xs tabular-nums',
              group === 'IN_FLIGHT' && 'text-muted-foreground',
              slow && 'font-semibold text-state-attention',
            )}
          >
            {formatAge(row.created_at, at)}
          </span>
        );
      },
    },
    // The actions column only exists for a role that may act — a VIEWER gets no empty column.
    ...(canCancel
      ? [
          {
            id: 'actions',
            header: '',
            srHeader: t('withdrawalQueue.columns.actionsSrHeader'),
            align: 'right',
            priority: 'secondary',
            cell: (row) => {
              // Cancel is offered on the two states the server accepts: AVAILABLE (nobody has
              // claimed the row) and RESERVED (a depositor holds it but has not yet said they
              // paid). LOCKED and ON_HOLD are deliberately left out — their depositor has already
              // claimed payment, the server answers 409, and a button that can only ever fail is
              // worse than no button at all.
              const cancellable = row.status === 'AVAILABLE' || row.status === 'RESERVED';
              const uploadable =
                (row.status === 'RESERVED' || row.status === 'LOCKED') &&
                row.matched_transaction_id !== null;
              if (!cancellable && !uploadable) return null;
              return (
                <div className="flex flex-wrap items-center justify-end gap-1">
                  {uploadable ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      // The row itself navigates to the matched deposit's history when one
                      // exists, so without the stop the click would both open the dialog and
                      // yank the operator off to `/ops/history/…` — and the navigation wins by
                      // unmounting the page out from under it.
                      onClick={(event) => {
                        event.stopPropagation();
                        setUploadTarget(row);
                      }}
                    >
                      <FileUp aria-hidden="true" />
                      {t('withdrawalQueue.slip.button')}
                    </Button>
                  ) : null}
                  {/* Two buttons, not one with a choice inside the dialog: they are different
                      acts on the customer's money, and which one an operator meant is decided
                      before the reason box, not after it. Each carries a tooltip because the
                      difference is in what happens on the *website* afterwards, which no label
                      short enough for this column can carry. */}
                  {cancellable ? (
                    <Tooltip content={t('withdrawalQueue.cancel.moveTooltip')}>
                      <Button
                        variant="ghost"
                        size="sm"
                        // Same stop as the upload: an AVAILABLE row released from a reservation
                        // still carries a matched deposit id, and a reserved one always does.
                        onClick={(event) => {
                          event.stopPropagation();
                          openCancelDialog(row, 'MOVE_TO_NORMAL');
                        }}
                      >
                        <Banknote aria-hidden="true" />
                        {t('withdrawalQueue.cancel.moveButton')}
                      </Button>
                    </Tooltip>
                  ) : null}
                  {cancellable ? (
                    <Tooltip content={t('withdrawalQueue.cancel.buttonTooltip')}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCancelDialog(row, 'RELEASE');
                        }}
                      >
                        <Ban aria-hidden="true" />
                        {t('withdrawalQueue.cancel.button')}
                      </Button>
                    </Tooltip>
                  ) : null}
                </div>
              );
            },
          } satisfies Column<WithdrawalQueueRow>,
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title={t('withdrawalQueue.title')}
        description={t('withdrawalQueue.description')}
      />

      {/* The unscoped pool is the normal state and the group tabs already carry pool-wide counts,
          so only the scoped case needs qualifying. */}
      <WebScopeScopedNotice className="mb-4" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {WITHDRAWAL_TABS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={group === candidate}
            onClick={() => {
              // Each tab has its own statuses and its own natural order; carrying either across
              // would silently show an empty or oddly-ranked page.
              view.set({ tab: candidate, status: 'ALL', dir: undefined, page: 0 });
            }}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              group === candidate
                ? 'border-primary bg-accent text-accent-foreground'
                : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`withdrawalQueue.groups.${candidate}`)}
            {counts ? (
              <Pill className="ml-2">
                {candidate === 'WAITING'
                  ? counts.waiting
                  : candidate === 'IN_FLIGHT'
                    ? counts.in_flight
                    : counts.done}
              </Pill>
            ) : null}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-full max-w-xs space-y-1.5">
          <Label htmlFor="withdrawals-search" className="sr-only">
            {t('withdrawalQueue.searchLabel')}
          </Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="withdrawals-search"
              className="pl-8"
              placeholder={t('withdrawalQueue.searchPlaceholder')}
              value={searchDraft}
              onChange={(event) => {
                onSearchChange(event.target.value);
              }}
            />
          </div>
        </div>

        <div className="w-full max-w-40 space-y-1.5">
          <Label htmlFor="withdrawals-amount" className="sr-only">
            {t('withdrawalQueue.amountLabel')}
          </Label>
          <Input
            id="withdrawals-amount"
            inputMode="numeric"
            className="tnum"
            placeholder={t('withdrawalQueue.amountPlaceholder')}
            value={amount}
            onChange={(event) => {
              view.set({ amount: event.target.value.replace(/[^\d.]/g, ''), page: 0 });
            }}
          />
        </div>

        {GROUP_STATUSES[group].length > 1 ? (
          <div className="space-y-1.5">
            <Label htmlFor="withdrawals-status">{t('withdrawalQueue.statusLabel')}</Label>
            <Select
              value={status}
              onValueChange={(value) => {
                view.set({ status: value, page: 0 });
              }}
            >
              <SelectTrigger id="withdrawals-status" className="min-w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t('withdrawalQueue.statusAll')}</SelectItem>
                {GROUP_STATUSES[group].map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {t(stateMeta('withdrawal', candidate).labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="ml-auto md:hidden">
          <SortControl
            id="withdrawals-sort"
            label={t('withdrawalQueue.sortLabel')}
            options={[
              { value: 'CREATED', label: t('withdrawalQueue.sortOptions.CREATED') },
              { value: 'AMOUNT', label: t('withdrawalQueue.sortOptions.AMOUNT') },
            ]}
            value={sort}
            dir={dir}
            // The control beside it owns the direction, so changing the column keeps whichever
            // way the operator is reading — unlike a header click, which starts a new column at
            // its natural order.
            onValueChange={(value) => {
              const next = readSort(value);
              if (next === undefined) return;
              view.set({ sort: next, dir, page: 0 });
            }}
            onDirChange={(value) => {
              view.set({ dir: value, page: 0 });
            }}
          />
        </div>
      </div>

      {filtered ? (
        <Button
          variant="ghost"
          size="sm"
          className="-mt-2 mb-3 w-fit"
          onClick={() => {
            view.set({ status: 'ALL', q: '', amount: '', page: 0 });
          }}
        >
          <X aria-hidden="true" />
          {t('withdrawalQueue.clearFilters')}
        </Button>
      ) : null}

      {queue.isLoading ? (
        <TableSkeleton rows={6} />
      ) : queue.isError ? (
        <ErrorState
          error={queue.error}
          onRetry={() => {
            void queue.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Hourglass className="size-5" aria-hidden="true" />}
          title={
            !filtered && scope !== ALL_WEBS
              ? t('common.webScope.emptyTitle')
              : t('withdrawalQueue.emptyTitle')
          }
          description={
            filtered
              ? t('withdrawalQueue.emptyFiltered')
              : // Scoped takes over from the group's own copy only when nothing else already
                // explains the empty group — "waiting for match" reads as "the pool is calm" when
                // the truth is "this site has no rows in it at all".
                scope !== ALL_WEBS
                ? t('common.webScope.emptyDescription', { web: scope })
                : t(`withdrawalQueue.emptyBody.${group}`)
          }
        />
      ) : (
        <div className="space-y-3">
          <DataTable
            caption={t('withdrawalQueue.title')}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            sort={{ key: sort, dir, onChange: sortBy }}
            // A waiting row has no matched deposit yet — everything about it is already visible
            // in the row, so only matched rows click through.
            onRowClick={(row) => {
              if (row.matched_transaction_id === null) return;
              // `from` carries this exact view along, so the deposit's back link returns to the
              // tab and page the operator was reading rather than to the top of History.
              void navigate({
                to: '/ops/history/$transactionId',
                params: { transactionId: row.matched_transaction_id },
                search: { from: view.href },
              });
            }}
          />
          <Pager
            summary={t('withdrawalQueue.pageSummary', { page: page + 1, pages, total })}
            page={page}
            pages={pages}
            limit={limit}
            onPageChange={(value) => {
              view.set({ page: value });
            }}
            onLimitChange={(value) => {
              view.set({ limit: value, page: 0 });
            }}
          />
        </div>
      )}

      <Dialog.Root
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeCancelDialog();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-5 shadow-lg focus:outline-none">
            <Dialog.Title className="text-sm font-semibold">
              {t(cancelIsMove ? 'withdrawalQueue.cancel.moveTitle' : 'withdrawalQueue.cancel.title')}
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-xs text-muted-foreground">
              {/* Four sentences, not two: the reservation warning and the disposition are
                  independent — a move can be the one that kills a depositor's reservation just as
                  easily as a plain cancel — so the copy states both rather than letting one
                  imply the other. */}
              {t(
                cancelIsMove
                  ? cancelIsReserved
                    ? 'withdrawalQueue.cancel.moveReservedDescription'
                    : 'withdrawalQueue.cancel.moveDescription'
                  : cancelIsReserved
                    ? 'withdrawalQueue.cancel.reservedDescription'
                    : 'withdrawalQueue.cancel.description',
              )}
            </Dialog.Description>

            {cancelTarget ? (
              <div className="mt-3 rounded-md border border-border bg-background px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-medium">{shortId(cancelTarget.id)}</span>
                  <MoneyDisplay amount={cancelTarget.amount} />
                </div>
                <p className="mt-1 truncate text-2xs text-muted-foreground">
                  {cancelTarget.name} · {bankInfo(cancelTarget.bank).name} ·{' '}
                  <span className="font-mono">{cancelTarget.number_masked}</span>
                </p>
                {/* What the operator is really deciding on: whether the depositor holding this
                    row has sent anything yet. "Reserved, still silent" and "reserved, slip
                    already in verification" are two different decisions, and nothing else in
                    this dialog tells them apart. Absent (rather than null) fields mean an API
                    that predates them, so the line is left out rather than guessed at. */}
                {cancelIsReserved && cancelTarget.evidence_submitted_at !== undefined ? (
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border pt-2 text-2xs">
                    <span className="text-muted-foreground">
                      {t('withdrawalQueue.cancel.evidenceLabel')}
                    </span>
                    {cancelTarget.evidence_submitted_at === null ? (
                      <span className="font-medium text-state-attention">
                        {t('withdrawalQueue.reservation.noEvidence')}
                      </span>
                    ) : (
                      <span className="font-medium">
                        {t('withdrawalQueue.reservation.evidenceAt', {
                          time: formatDateTime(cancelTarget.evidence_submitted_at),
                        })}
                      </span>
                    )}
                    {cancelTarget.deposit_status == null ? null : (
                      <StateBadge
                        machine="deposit"
                        status={cancelTarget.deposit_status}
                        size="sm"
                      />
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {cancelIsReserved ? (
              <Callout
                tone="warning"
                title={t('withdrawalQueue.cancel.reservedRisk.title')}
                className="mt-3 text-xs"
              >
                <ul className="list-disc space-y-1 pl-4">
                  <li>{t('withdrawalQueue.cancel.reservedRisk.mayHavePaid')}</li>
                  <li>{t('withdrawalQueue.cancel.reservedRisk.depositExpires')}</li>
                  <li>
                    {t(
                      cancelIsMove
                        ? 'withdrawalQueue.cancel.reservedRisk.moveIrreversible'
                        : 'withdrawalQueue.cancel.reservedRisk.irreversible',
                    )}
                  </li>
                </ul>
              </Callout>
            ) : null}

            {/* PF has no ordinary-withdrawal queue: it releases the lock and says what was
                wanted, and the website re-files the payout. Stated plainly because a website
                that has not implemented the instruction releases instead, which is a correct
                fallback but not the outcome the operator just asked for. */}
            {cancelIsMove ? (
              <Callout tone="info" className="mt-3 text-xs">
                {t('withdrawalQueue.cancel.moveNote')}
              </Callout>
            ) : null}

            <div className="mt-3 space-y-1.5">
              <Label htmlFor="withdrawal-cancel-reason">
                {t('withdrawalQueue.cancel.reasonLabel')}
              </Label>
              <Textarea
                id="withdrawal-cancel-reason"
                required
                value={cancelReason}
                placeholder={t('withdrawalQueue.cancel.reasonPlaceholder')}
                aria-describedby="withdrawal-cancel-reason-hint"
                onChange={(event) => {
                  setCancelReason(event.target.value);
                }}
              />
              <p id="withdrawal-cancel-reason-hint" className="text-2xs text-muted-foreground">
                {t('withdrawalQueue.cancel.reasonHint')}
              </p>
            </div>

            {cancel.isError ? (
              <div className="mt-3">
                <ErrorState error={cancel.error} />
              </div>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              <Dialog.Close asChild>
                <Button variant="ghost" size="sm">
                  {t('withdrawalQueue.cancel.dismiss')}
                </Button>
              </Dialog.Close>
              <Button
                // A move is not a destructive act — the customer keeps the money and keeps the
                // payout; only the channel changes — so it does not borrow the red that means
                // "this ends here".
                variant={cancelIsMove ? 'default' : 'destructive'}
                size="sm"
                disabled={cancelReason.trim().length < 8}
                loading={cancel.isPending}
                onClick={() => {
                  if (cancelTarget === null) return;
                  cancel.mutate(
                    {
                      id: cancelTarget.id,
                      reason: cancelReason.trim(),
                      disposition: cancelDisposition,
                    },
                    {
                      onSuccess: () => {
                        closeCancelDialog();
                      },
                    },
                  );
                }}
              >
                {cancelIsMove ? <Banknote aria-hidden="true" /> : <Ban aria-hidden="true" />}
                {t(
                  cancelIsMove
                    ? 'withdrawalQueue.cancel.moveConfirm'
                    : 'withdrawalQueue.cancel.confirm',
                )}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={uploadTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeUploadDialog();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-5 shadow-lg focus:outline-none">
            <Dialog.Title className="text-sm font-semibold">
              {t('withdrawalQueue.slip.title')}
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-xs text-muted-foreground">
              {t('withdrawalQueue.slip.description')}
            </Dialog.Description>

            {uploadTarget ? (
              <div className="mt-3 rounded-md border border-border bg-background px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-medium">{shortId(uploadTarget.id)}</span>
                  <MoneyDisplay amount={uploadTarget.amount} />
                </div>
                <p className="mt-1 truncate text-2xs text-muted-foreground">
                  {uploadTarget.name} · {bankInfo(uploadTarget.bank).name} ·{' '}
                  <span className="font-mono">{uploadTarget.number_masked}</span>
                </p>
              </div>
            ) : null}

            <div className="mt-3 space-y-1.5">
              <input
                ref={slipInputRef}
                id="withdrawal-slip-file"
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                aria-label={t('withdrawalQueue.slip.fileLabel')}
                className="sr-only"
                onChange={(event) => {
                  setSlipFile(event.target.files?.[0] ?? null);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="max-w-full"
                onClick={() => {
                  slipInputRef.current?.click();
                }}
              >
                <Upload aria-hidden="true" />
                <span className="truncate">
                  {slipFile ? slipFile.name : t('withdrawalQueue.slip.chooseFile')}
                </span>
              </Button>
              <p className="text-2xs text-muted-foreground">{t('withdrawalQueue.slip.fileHint')}</p>
              {slipError ? (
                <p className="text-2xs font-medium text-destructive">
                  {t(`withdrawalQueue.slip.${slipError}`)}
                </p>
              ) : null}
            </div>

            {upload.isError ? (
              <div className="mt-3">
                <ErrorState error={upload.error} />
              </div>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              <Dialog.Close asChild>
                <Button variant="ghost" size="sm">
                  {t('withdrawalQueue.slip.dismiss')}
                </Button>
              </Dialog.Close>
              <Button
                size="sm"
                disabled={slipFile === null || slipError !== null}
                loading={upload.isPending}
                onClick={() => {
                  if (uploadTarget?.matched_transaction_id == null || slipFile === null) return;
                  upload.mutate(
                    { transactionId: uploadTarget.matched_transaction_id, file: slipFile },
                    {
                      onSuccess: () => {
                        closeUploadDialog();
                      },
                    },
                  );
                }}
              >
                <FileUp aria-hidden="true" />
                {t('withdrawalQueue.slip.confirm')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
